import React, { useState } from 'react';
import {
    Text,
    Icon,
    useTheme,
    IconButton,
    Stack,
    IContextualMenuItem,
} from '@fluentui/react';
import { ServiceData, ComponentData } from '../models/CloudStatusModels';
import { Ellipsis } from '../../HubLayout/components/Utilities/Ellipsis';
import {
    getStatusColor,
    getStatusFluentIcon,
    formatDate,
    isMobileWidth,
} from '../utils/StatusHelpers';
import '../styles/CommonStyles.css';

interface ServiceStatusListProps {
    services: ServiceData[];
    selectedStage: string;
    onComponentSelect?: (componentId: string | null) => void;
    selectedComponentId?: string | null;
}

const ComponentRow: React.FC<{
    component: ComponentData;
    isSelected: boolean;
    onSelect: () => void;
}> = ({ component, isSelected, onSelect }) => {
    const theme = useTheme();
    const statusColor = getStatusColor(component.health);
    const isMobile = isMobileWidth();

    const componentMenuItems: IContextualMenuItem[] = [
        {
            key: 'changeContext',
            text: 'Change Context',
            iconProps: { iconName: 'Switch' },
            onClick: () => {
                console.log(
                    'Change Context clicked for',
                    component.componentName
                );
                // TODO: Implement context change
            },
        },
        {
            key: 'restartService',
            text: 'Restart Service',
            iconProps: { iconName: 'Refresh' },
            onClick: () => {
                console.log(
                    'Restart Service clicked for',
                    component.componentName
                );
                // TODO: Implement service restart
            },
        },
        {
            key: 'disableInCloud',
            text: 'Disable In Cloud',
            iconProps: { iconName: 'CloudNotSynced' },
            onClick: () => {
                console.log(
                    'Disable In Cloud clicked for',
                    component.componentName
                );
                // TODO: Implement cloud disable
            },
        },
        {
            key: 'ignoreInPublic',
            text: 'Ignore in Public',
            iconProps: { iconName: 'Hide' },
            onClick: () => {
                console.log(
                    'Ignore in Public clicked for',
                    component.componentName
                );
                // TODO: Implement public ignore
            },
        },
    ];

    return (
        <div
            onClick={onSelect}
            style={{
                display: 'grid',
                gridTemplateColumns: isMobile
                    ? '40px 1fr 100px 50px'
                    : '40px 1fr 120px 200px 50px',
                gap: isMobile ? '8px' : '16px',
                padding: isMobile
                    ? '12px 8px 12px 40px'
                    : '12px 16px 12px 56px',
                backgroundColor: theme.palette.neutralLighterAlt,
                borderBottom: `1px solid ${theme.palette.neutralLight}`,
                borderLeft: isSelected
                    ? `3px solid ${theme.palette.themePrimary}`
                    : '3px solid transparent',
                outline: isSelected
                    ? `2px solid ${theme.palette.themePrimary}`
                    : 'none',
                outlineOffset: '-2px',
                alignItems: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
                if (!isSelected) {
                    e.currentTarget.style.backgroundColor =
                        theme.palette.neutralLighter;
                }
            }}
            onMouseLeave={(e) => {
                if (!isSelected) {
                    e.currentTarget.style.backgroundColor =
                        theme.palette.neutralLighterAlt;
                }
            }}
        >
            <Icon
                iconName={getStatusFluentIcon(component.health)}
                styles={{
                    root: {
                        fontSize: '16px',
                        color: statusColor,
                    },
                }}
            />
            <Text
                variant="small"
                styles={{
                    root: {
                        color: theme.palette.neutralPrimary,
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                    },
                }}
            >
                {component.componentName}
            </Text>
            <Text
                variant="small"
                styles={{
                    root: {
                        color: statusColor,
                        fontWeight: 600,
                        textAlign: 'center',
                    },
                }}
            >
                {component.health}
            </Text>
            {!isMobile && (
                <Text
                    variant="small"
                    styles={{
                        root: {
                            color: theme.palette.neutralSecondary,
                            fontSize: '11px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        },
                    }}
                >
                    {formatDate(component.lastUpdated)}
                </Text>
            )}
            <div
                onClick={(e) => {
                    e.stopPropagation();
                }}
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}
            >
                <Ellipsis menuItems={componentMenuItems} />
            </div>
        </div>
    );
};

const ServiceRow: React.FC<{
    service: ServiceData;
    selectedComponentId: string | null;
    onComponentSelect: (componentId: string | null) => void;
}> = ({ service, selectedComponentId, onComponentSelect }) => {
    const theme = useTheme();
    const [isExpanded, setIsExpanded] = useState(false);
    const statusColor = getStatusColor(service.health);
    const isMobile = isMobileWidth();

    return (
        <div>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile
                        ? '40px 1fr 100px'
                        : '40px 40px 1fr 120px 200px',
                    gap: isMobile ? '8px' : '16px',
                    padding: '16px',
                    backgroundColor: theme.palette.white,
                    borderBottom: `1px solid ${theme.palette.neutralLight}`,
                    alignItems: 'center',
                    cursor:
                        service.components.length > 0 ? 'pointer' : 'default',
                    transition: 'background-color 0.2s ease',
                }}
                onClick={() =>
                    service.components.length > 0 && setIsExpanded(!isExpanded)
                }
                onMouseEnter={(e) => {
                    if (service.components.length > 0) {
                        e.currentTarget.style.backgroundColor =
                            theme.palette.neutralLighter;
                    }
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = theme.palette.white;
                }}
            >
                {service.components.length > 0 ? (
                    <IconButton
                        iconProps={{
                            iconName: isExpanded
                                ? 'ChevronDown'
                                : 'ChevronRight',
                        }}
                        ariaLabel={`${
                            isExpanded ? 'Collapse' : 'Expand'
                        } components for ${service.serviceName || 'service'}`}
                        aria-expanded={isExpanded}
                        title={isExpanded ? 'Collapse' : 'Expand'}
                        styles={{
                            root: {
                                color: theme.palette.neutralPrimary,
                                width: '32px',
                                height: '32px',
                            },
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsExpanded(!isExpanded);
                        }}
                    />
                ) : (
                    <div style={{ width: '32px' }} />
                )}
                {!isMobile && (
                    <Icon
                        iconName={getStatusFluentIcon(service.health)}
                        styles={{
                            root: {
                                fontSize: '20px',
                                color: statusColor,
                            },
                        }}
                    />
                )}
                <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <Text
                        variant="medium"
                        styles={{
                            root: {
                                color: theme.palette.neutralPrimary,
                                fontWeight: 600,
                                display: 'block',
                                marginBottom: '4px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            },
                        }}
                    >
                        {service.serviceName}
                    </Text>
                    <Text
                        variant="small"
                        styles={{
                            root: {
                                color: theme.palette.neutralSecondary,
                                fontSize: '11px',
                            },
                        }}
                    >
                        Stage: {service.stage}
                    </Text>
                </div>
                <Text
                    variant="medium"
                    styles={{
                        root: {
                            color: statusColor,
                            fontWeight: 700,
                            textAlign: 'center',
                        },
                    }}
                >
                    {service.health}
                </Text>
                {!isMobile && (
                    <Text
                        variant="small"
                        styles={{
                            root: {
                                color: theme.palette.neutralSecondary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            },
                        }}
                    >
                        {formatDate(service.lastUpdated)}
                    </Text>
                )}
            </div>

            {/* Expanded Components */}
            {isExpanded && service.components.length > 0 && (
                <div
                    style={{
                        backgroundColor: theme.palette.neutralLighterAlt,
                        borderBottom: `2px solid ${theme.palette.neutralLight}`,
                    }}
                >
                    {service.components.map((component) => (
                        <ComponentRow
                            key={component.componentId}
                            component={component}
                            isSelected={
                                selectedComponentId === component.componentId
                            }
                            onSelect={() =>
                                onComponentSelect(component.componentId)
                            }
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export const ServiceStatusList: React.FC<ServiceStatusListProps> = ({
    services,
    selectedStage,
    onComponentSelect = () => {},
    selectedComponentId = null,
}) => {
    const theme = useTheme();

    if (services.length === 0) {
        return (
            <div
                style={{
                    padding: '32px',
                    textAlign: 'center',
                    backgroundColor: theme.palette.neutralLighter,
                    borderRadius: '4px',
                }}
            >
                <Text
                    variant="medium"
                    styles={{
                        root: {
                            color: theme.palette.neutralSecondary,
                        },
                    }}
                >
                    No services found for the selected stage
                </Text>
            </div>
        );
    }

    return (
        <div
            style={{
                backgroundColor: theme.palette.white,
                borderRadius: '4px',
                boxShadow:
                    '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                overflow: 'hidden',
                width: '100%',
                maxWidth: '100%',
            }}
        >
            {/* Header */}
            <div
                style={{
                    padding: '16px 20px',
                    backgroundColor: theme.palette.neutralQuaternary,
                    borderBottom: `2px solid ${theme.palette.themeDark}`,
                }}
            >
                <Text
                    variant="xLarge"
                    styles={{
                        root: {
                            fontWeight: 700,
                            color: theme.palette.black,
                        },
                    }}
                >
                    Service Status - {selectedStage}
                </Text>
                <Text
                    variant="small"
                    styles={{
                        root: {
                            color: theme.palette.black,
                            opacity: 0.9,
                            marginTop: '4px',
                            display: 'block',
                        },
                    }}
                >
                    {services.length} service{services.length !== 1 ? 's' : ''}{' '}
                    in this stage
                </Text>
            </div>

            {/* Column Headers */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: isMobileWidth()
                        ? '40px 1fr 100px'
                        : '40px 40px 1fr 120px 200px',
                    gap: isMobileWidth() ? '8px' : '16px',
                    padding: '12px 16px',
                    backgroundColor: theme.palette.neutralLighter,
                    borderBottom: `2px solid ${theme.palette.neutralLight}`,
                    overflowX: 'auto',
                }}
            >
                <div />
                {!isMobileWidth() && <div />}
                <Text
                    variant="small"
                    styles={{
                        root: {
                            fontWeight: 700,
                            color: theme.palette.neutralPrimary,
                        },
                    }}
                >
                    Service Name
                </Text>
                <Text
                    variant="small"
                    styles={{
                        root: {
                            fontWeight: 700,
                            color: theme.palette.neutralPrimary,
                            textAlign: 'center',
                        },
                    }}
                >
                    Health
                </Text>
                {!isMobileWidth() && (
                    <Text
                        variant="small"
                        styles={{
                            root: {
                                fontWeight: 700,
                                color: theme.palette.neutralPrimary,
                            },
                        }}
                    >
                        Last Updated
                    </Text>
                )}
            </div>

            {/* Service Rows */}
            <div>
                {services.map((service) => (
                    <ServiceRow
                        key={service.serviceId}
                        service={service}
                        selectedComponentId={selectedComponentId}
                        onComponentSelect={onComponentSelect}
                    />
                ))}
            </div>
        </div>
    );
};
